import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { ButtonGroup } from '@mui/material';
import { type ReactElement, useMemo, useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { config } from '@/config';
import {
  useCreateUploadToken,
  useRevokeUploadToken,
  useTestUploadToken,
  useUploadTokensQuery,
} from '@/features/uploads';
import { useNotifications } from '@/providers/notificationHooks';
import { useMutationErrorSnackbar } from '@/shared/hooks/useMutationErrorSnackbar';
import { componentSizes } from '@/theme/componentSizes';
import { s } from '@/theme/tokens';
import { Box, CodeOutputBlock, formatDate, Panel, Stack, StatusBanner, Text } from '@/ui';
import { settingsCopyTextSx, settingsValueTextSx } from '@/ui/composites/settingsPageTypographySx';
import { CopyIcon, DeleteIcon, RotateRight, Tooltip } from '@/ui/primitives';
import { Button } from '@/ui/primitives/Button';
import { Tab, Tabs } from '@/ui/primitives/Tabs';
import { TextField } from '@/ui/primitives/TextField';
import { resolveTokenStatus } from './uploadTokens/tokenUtils';
import type { UploadTokenDto } from '@flowqa/shared';

type ProjectUploadTokensSectionProps = { projectId: string; projectName?: string | null };
type ProjectUploadTokensContainer = 'panel' | 'none';
type UploadSnippetFormat = 'XLSX' | 'CSV' | 'XML' | 'JSON';

export const ProjectUploadTokensSection = ({
  projectId,
  projectName,
  container = 'panel',
  initialSnippetTab = 'XLSX',
}: ProjectUploadTokensSectionProps & {
  container?: ProjectUploadTokensContainer;
  initialSnippetTab?: UploadSnippetFormat;
}): ReactElement => {
  const { notify } = useNotifications();
  const mutationError = useMutationErrorSnackbar();
  const tokensQuery = useUploadTokensQuery(projectId);
  const createToken = useCreateUploadToken();
  const revokeToken = useRevokeUploadToken();
  const testToken = useTestUploadToken();
  const [tokenValue, setTokenValue] = useState('');
  const [snippetTagsInput, setSnippetTagsInput] = useState('');
  const [generatedTokenMeta, setGeneratedTokenMeta] = useState<Pick<
    UploadTokenDto,
    'createdAt' | 'lastUsedAt'
  > | null>(null);
  const [activeSnippetTab, setActiveSnippetTab] = useState<UploadSnippetFormat>(initialSnippetTab);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const uploadEndpoint = `${config.apiBaseUrl.replace(/\/+$/, '')}/uploads`;
  const actionIconSize = componentSizes.auth.inputHeight - 2;
  const actionGroupSx = {
    backgroundColor: 'background.paper',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    overflow: 'hidden',
    '& .MuiButton-root': {
      color: 'text.primary',
      borderColor: 'divider',
      minWidth: 0,
    },
    '& .MuiButtonGroup-grouped': {
      width: actionIconSize,
      minWidth: actionIconSize,
      maxWidth: actionIconSize,
      flex: `0 0 ${actionIconSize}px`,
      padding: 0,
    },
    '& .MuiButtonGroup-grouped:not(:last-of-type)': {
      borderRight: '1px solid',
      borderColor: 'divider',
    },
    '& .MuiButton-root.Mui-disabled': {
      color: 'text.disabled',
    },
  } as const;
  const actionButtonSx = {
    width: actionIconSize,
    height: actionIconSize,
    minWidth: actionIconSize,
    minHeight: actionIconSize,
    padding: 0,
    borderRadius: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    '& .MuiSvgIcon-root': {
      fontSize: 17,
      display: 'block',
    },
    '&:hover': {
      backgroundColor: 'action.hover',
    },
  } as const;
  const tokens = tokensQuery.data ?? [];
  const activeTokens = useMemo(
    () => tokens.filter((token) => resolveTokenStatus(token) === 'Active'),
    [tokens],
  );
  const latestActiveToken = activeTokens.length > 0 ? activeTokens[0] : null;
  const visibleTokenMeta: Pick<UploadTokenDto, 'createdAt' | 'lastUsedAt'> | null =
    generatedTokenMeta ?? latestActiveToken;
  const createdAt = visibleTokenMeta?.createdAt ?? null;
  const lastUsedAt = visibleTokenMeta?.lastUsedAt ?? null;
  const createdLabel = formatDate(createdAt, { includeTime: true }) || '-';
  const lastUsedLabel = formatDate(lastUsedAt, { includeTime: true }) || '-';
  const hasPlaintextToken = tokenValue.trim().length > 0;
  const hasConfiguredToken = hasPlaintextToken || activeTokens.length > 0;
  const displayTokenValue = hasPlaintextToken
    ? tokenValue
    : hasConfiguredToken
      ? '••••••••••••••••'
      : '';
  const savePending = createToken.isPending || revokeToken.isPending;
  const testPending = testToken.isPending;
  const snippetTagValues = useMemo(
    () =>
      Array.from(
        new Set(
          snippetTagsInput
            .split(',')
            .map((tag) => tag.trim())
            .filter((tag) => tag.length > 0),
        ),
      ),
    [snippetTagsInput],
  );
  const uploadEndpointWithTags = useMemo(() => {
    if (snippetTagValues.length === 0) return uploadEndpoint;
    const params = snippetTagValues.map((tag) => `tags=${encodeURIComponent(tag)}`).join('&');
    return `${uploadEndpoint}?${params}`;
  }, [snippetTagValues, uploadEndpoint]);
  const uploadXmlSnippet = [
    'curl --request POST \\',
    `  "${uploadEndpointWithTags}" \\`,
    '  --header "x-upload-token: $UPLOAD_TOKEN" \\',
    '  --header "content-type: application/xml" \\',
    '  --header "x-file-name: $XML_FILE_NAME" \\',
    '  --data-binary @"$XML_FILE_PATH"',
  ].join('\n');
  const uploadJsonSnippet = [
    'curl --request POST \\',
    `  "${uploadEndpointWithTags}" \\`,
    '  --header "x-upload-token: $UPLOAD_TOKEN" \\',
    '  --header "content-type: application/json" \\',
    '  --header "x-file-name: $JSON_FILE_NAME" \\',
    '  --data-binary @"$JSON_FILE_PATH"',
  ].join('\n');
  const uploadCsvSnippet = [
    'curl --request POST \\',
    `  "${uploadEndpointWithTags}" \\`,
    '  --header "x-upload-token: $UPLOAD_TOKEN" \\',
    '  --header "content-type: text/csv" \\',
    '  --header "x-file-name: $CSV_FILE_NAME" \\',
    '  --data-binary @"$CSV_FILE_PATH"',
  ].join('\n');
  const uploadXlsxSnippet = [
    'curl --request POST \\',
    `  "${uploadEndpointWithTags}" \\`,
    '  --header "x-upload-token: $UPLOAD_TOKEN" \\',
    '  --header "content-type: application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" \\',
    '  --header "x-file-name: $XLSX_FILE_NAME" \\',
    '  --data-binary @"$XLSX_FILE_PATH"',
  ].join('\n');

  const copyUploadToken = (): void => {
    if (!hasPlaintextToken) return;
    void navigator.clipboard.writeText(tokenValue);
    notify({ message: 'Upload token copied to clipboard', tone: 'green' });
  };

  const copyUploadSnippet = (snippet: string, format: UploadSnippetFormat): void => {
    void navigator.clipboard.writeText(snippet);
    notify({ message: `Copied ${format} upload snippet`, tone: 'green' });
  };

  const applyGenerateToken = async (): Promise<void> => {
    try {
      for (const token of activeTokens) {
        await revokeToken.mutateAsync({ projectId, tokenId: token.id });
      }
      const created = await createToken.mutateAsync({
        projectId,
        input: { name: `ci-upload-token-${Date.now()}` },
      });
      setTokenValue(created.token);
      setGeneratedTokenMeta({
        createdAt: created.tokenRecord.createdAt,
        lastUsedAt: created.tokenRecord.lastUsedAt,
      });
      mutationError.clearError();
      setRegenerateOpen(false);
      notify({
        message: hasConfiguredToken ? 'Regenerated upload token' : 'Generated upload token',
        tone: 'green',
      });
    } catch (err) {
      mutationError.setError(err);
    }
  };

  const onGenerateOrRegenerate = (): void => {
    if (hasConfiguredToken) {
      setRegenerateOpen(true);
      return;
    }
    void applyGenerateToken();
  };

  const applyRevoke = async (): Promise<void> => {
    try {
      for (const token of activeTokens) {
        await revokeToken.mutateAsync({ projectId, tokenId: token.id });
      }
      setTokenValue('');
      setGeneratedTokenMeta(null);
      setRevokeOpen(false);
      mutationError.clearError();
      notify({ message: 'Revoked upload token', tone: 'green' });
    } catch (err) {
      mutationError.setError(err);
    }
  };

  const onTestUpload = (): void => {
    if (!hasPlaintextToken) return;
    testToken.mutate(
      { projectId, input: { token: tokenValue } },
      {
        onSuccess: () => {
          setGeneratedTokenMeta((previous) =>
            previous ? { ...previous, lastUsedAt: new Date() } : previous,
          );
          mutationError.clearError();
          notify({ message: 'Upload token test passed', tone: 'green' });
        },
        onError: (err) => {
          mutationError.setError(err);
        },
      },
    );
  };

  const content = (
    <Stack gap="md">
      <Stack gap="lg">
        <Stack gap="sm">
          <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
            Generate an API token and replace the variables in the upload snippets to upload results
            files to this project using cURL.
          </Text>
          <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
            If used within a pipeline, add to the pipeline's environment variables for
            maintainability.
          </Text>
        </Stack>
        <Stack gap="md">
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: s('md') }}>
            <Box sx={{ flex: 1 }}>
              <TextField
                label="Upload Token"
                fieldStyle="settings"
                value={displayTokenValue}
                placeholder="Generate an upload token to enable CI uploads"
                slotProps={{ input: { readOnly: true } }}
                sx={{ '& .MuiOutlinedInput-input': { color: 'text.secondary' } }}
                onFocus={(event) => {
                  event.target.select();
                }}
              />
            </Box>
            <ButtonGroup variant="outlined" aria-label="Upload token actions" sx={actionGroupSx}>
              <Button
                variant="ghost"
                fullWidthOnMobile={false}
                disableRipple
                loading={!hasConfiguredToken && savePending}
                aria-label={
                  hasConfiguredToken ? 'Regenerate Upload Token' : 'Generate Upload Token'
                }
                disabled={savePending}
                sx={[
                  actionButtonSx,
                  ...(!hasConfiguredToken
                    ? [
                        {
                          '&&': {
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                          },
                          '&&:hover': {
                            backgroundColor: 'primary.dark',
                          },
                        },
                      ]
                    : []),
                ]}
                onClick={onGenerateOrRegenerate}
              >
                <Tooltip
                  title={hasConfiguredToken ? 'Regenerate Upload Token' : 'Generate Upload Token'}
                  placement="top"
                >
                  <RotateRight sx={{ fontSize: 21 }} />
                </Tooltip>
              </Button>
              <Button
                variant="ghost"
                fullWidthOnMobile={false}
                disableRipple
                loading={testPending}
                aria-label="Test Upload Token"
                disabled={!hasPlaintextToken || savePending || testPending}
                sx={[actionButtonSx, { minWidth: 56, width: 56, maxWidth: 56 }]}
                onClick={onTestUpload}
              >
                <Tooltip title="Test Upload Token" placement="top">
                  <PlayArrowIcon sx={{ fontSize: 22 }} />
                </Tooltip>
              </Button>
              <Button
                variant="ghost"
                fullWidthOnMobile={false}
                disableRipple
                aria-label="Copy Upload Token"
                disabled={!hasPlaintextToken || savePending}
                sx={actionButtonSx}
                onClick={copyUploadToken}
              >
                <Tooltip title="Copy Upload Token" placement="top">
                  <CopyIcon />
                </Tooltip>
              </Button>
              <Button
                variant="ghost"
                fullWidthOnMobile={false}
                disableRipple
                aria-label="Delete Upload Token"
                disabled={!hasConfiguredToken || savePending}
                sx={[
                  actionButtonSx,
                  ...(hasConfiguredToken
                    ? [
                        {
                          '&&': {
                            backgroundColor: 'error.main',
                            color: 'error.contrastText',
                            '& .MuiSvgIcon-root': {
                              color: 'common.white',
                            },
                          },
                          '&&:hover': {
                            backgroundColor: 'error.dark',
                            '& .MuiSvgIcon-root': {
                              color: 'common.white',
                            },
                          },
                        },
                      ]
                    : []),
                ]}
                onClick={() => {
                  setRevokeOpen(true);
                }}
              >
                <Tooltip title="Delete Upload Token" placement="top">
                  <DeleteIcon sx={{ fontSize: 20 }} />
                </Tooltip>
              </Button>
            </ButtonGroup>
          </Box>
          {hasPlaintextToken ? (
            <StatusBanner
              tone="blue"
              variant="outlined"
              message="Copy this token now. It is only shown once and will be hidden afterward."
              sx={{ mt: s('xs') }}
            />
          ) : null}
          {hasConfiguredToken ? (
            <Box sx={{ display: 'flex', gap: s('md'), width: '100%' }}>
              <Box sx={{ flex: 1 }}>
                <Stack gap="xxs">
                  <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
                    Created
                  </Text>
                  <Text role="body" sx={settingsValueTextSx}>
                    {createdLabel}
                  </Text>
                </Stack>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Stack gap="xxs">
                  <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
                    Last Used
                  </Text>
                  <Text role="body" sx={settingsValueTextSx}>
                    {lastUsedLabel}
                  </Text>
                </Stack>
              </Box>
            </Box>
          ) : null}
        </Stack>
        {hasConfiguredToken ? (
          <Stack gap="sm">
            <Text role="bodyStrong" color="textSecondary">
              Upload Snippets
            </Text>
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              Use the below cURL snippets for each file format. Optionally include tags as query
              params.
            </Text>
            <Stack gap="md">
              <TextField
                label="Tags"
                fieldStyle="settings"
                value={snippetTagsInput}
                placeholder="smoke, e2e, api"
                onChange={(event) => {
                  setSnippetTagsInput(event.target.value);
                }}
              />
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={activeSnippetTab}
                  onChange={(_, next) => {
                    setActiveSnippetTab(next as UploadSnippetFormat);
                  }}
                  aria-label="Upload snippet format tabs"
                >
                  <Tab label="XLSX" value="XLSX" />
                  <Tab label="CSV" value="CSV" />
                  <Tab label="XML" value="XML" />
                  <Tab label="JSON" value="JSON" />
                </Tabs>
              </Box>
              {activeSnippetTab === 'XLSX' ? (
                <CodeOutputBlock
                  value={uploadXlsxSnippet}
                  onCopy={() => {
                    copyUploadSnippet(uploadXlsxSnippet, 'XLSX');
                  }}
                  copyAriaLabel="Copy XLSX upload snippet"
                  reserveCopySpace={false}
                />
              ) : null}
              {activeSnippetTab === 'CSV' ? (
                <CodeOutputBlock
                  value={uploadCsvSnippet}
                  onCopy={() => {
                    copyUploadSnippet(uploadCsvSnippet, 'CSV');
                  }}
                  copyAriaLabel="Copy CSV upload snippet"
                  reserveCopySpace={false}
                />
              ) : null}
              {activeSnippetTab === 'XML' ? (
                <CodeOutputBlock
                  value={uploadXmlSnippet}
                  onCopy={() => {
                    copyUploadSnippet(uploadXmlSnippet, 'XML');
                  }}
                  copyAriaLabel="Copy XML upload snippet"
                  reserveCopySpace={false}
                />
              ) : null}
              {activeSnippetTab === 'JSON' ? (
                <CodeOutputBlock
                  value={uploadJsonSnippet}
                  onCopy={() => {
                    copyUploadSnippet(uploadJsonSnippet, 'JSON');
                  }}
                  copyAriaLabel="Copy JSON upload snippet"
                  reserveCopySpace={false}
                />
              ) : null}
            </Stack>
          </Stack>
        ) : null}
      </Stack>
      <ConfirmDialog
        open={regenerateOpen}
        title="Regenerate Upload Token"
        description={
          <Stack gap="md">
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              Generate a new upload token for this project.
            </Text>
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              This action revokes the existing upload token immediately.
            </Text>
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              Update your upload token after regenerating to avoid upload failures.
            </Text>
          </Stack>
        }
        confirmLabel="Regenerate Upload Token"
        confirmColor="primary"
        confirmVariant="contained"
        loading={savePending}
        onClose={() => {
          setRegenerateOpen(false);
        }}
        onConfirm={() => {
          void applyGenerateToken();
        }}
      />
      <ConfirmDialog
        open={revokeOpen}
        title="Delete Upload Token"
        description={
          <Stack gap="md">
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              Delete this upload token to immediately block CI uploads using it.
            </Text>
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              This action cannot be undone.
            </Text>
          </Stack>
        }
        confirmLabel="Delete Upload Token"
        confirmColor="error"
        confirmVariant="contained"
        loading={savePending}
        onClose={() => {
          setRevokeOpen(false);
        }}
        onConfirm={() => {
          void applyRevoke();
        }}
      />
    </Stack>
  );
  if (container === 'none') {
    return content;
  }
  return (
    <Panel
      title="API Upload Token"
      description={`Generate an API token to upload results files directly to ${
        projectName ?? 'this project'
      }.`}
    >
      {content}
    </Panel>
  );
};
